import {
  AnySchema,
  boolean,
  date,
  DateSchema,
  number,
  object,
  string,
} from 'yup';
import { DateTime } from 'luxon';

import { FormField } from 'theSource/types';
import { Validations } from './types';
import { i18n } from 'i18n';
import { DATE_FORMAT_PLACEHOLDER } from '../constants';

export const transformDateTime = (schema: DateSchema) => {
  return schema
    .transform((_, originalValue: DateTime | null) => {
      if (DateTime.isDateTime(originalValue)) {
        return originalValue.toJSDate();
      }
      return undefined;
    })
    .typeError(
      i18n.t('invalidDate', {
        format: DATE_FORMAT_PLACEHOLDER,
        interpolation: { escapeValue: false },
      })
    );
};

const applyValidationRules = <T extends AnySchema>(
  schema: T,
  field: FormField,
  applyRules?: (schema: T, field: FormField) => T
): T => (applyRules ? applyRules(schema, field) : schema);

const getSchema = (field: FormField, validations: Validations): AnySchema => {
  switch (field.dataType) {
    case 'BOOLEAN':
      return applyValidationRules(
        boolean(),
        field,
        validations.applyBooleanRules
      );
    case 'DATE':
      return applyValidationRules(
        transformDateTime(date()),
        field,
        validations.applyDateRules
      );
    case 'NUMBER':
      return applyValidationRules(
        number().typeError(i18n.t('common:invalidNumber')),
        field,
        validations.applyNumberRules
      );
    default:
      return applyValidationRules(
        string(),
        field,
        validations.applyStringRules
      );
  }
};

export const buildValidationSchema = (
  fields: FormField[],
  validations: Validations
) => {
  const validationSchema = fields.reduce((obj, item) => {
    // validate editable fields only
    if (item.editable) {
      return {
        ...obj,
        [item.fieldName]: getSchema(item, validations),
      };
    }
    return {
      ...obj,
    };
  }, {});

  return object(validationSchema);
};
